import { useCallback, useEffect, useMemo, useState } from 'react';
import { BODY_ZONES, FACE_ZONES, type Zone } from '@oxyflows/domain';
import { api, type CatalogueItem, type ZoneChartData } from '../api';
import { useI18n } from '../i18n';
import { ErrorState, Field, Modal, Skeleton, Spinner } from '../components';
import { ImagingUpload } from './ImagingUpload';
import { ImageViewer, type ViewerImage } from './ImageViewer';
import { BODY_DRAWING, BODY_SIZE, FACE_DRAWING, FACE_SIZE, zoneAnchor, zonePath } from './zone-geometry';
import { formatMinor, minorToInput, parseToMinor } from '../money';
import { describe } from '../errors';

/**
 * The face and body chart: the odontogram of dermatology and aesthetic
 * medicine. A zone is to this chart what a tooth is to that one: select it,
 * then record a finding (melasma on the left cheek, dynamic wrinkles on the
 * glabella), a procedure with its dose (20 units of toxin, 1 ml of filler,
 * 400 laser shots) and the product and lot it was given with, or a photograph.
 *
 * The drawing stays clean: zones are invisible until the pointer is over the
 * figure, over a name in the list beside it, or until they carry a record.
 * The list names every zone, grouped the way injectors and dermatologists
 * speak about them, so nothing has to be hunted for on the figure.
 *
 * CLINICAL SAFETY — the figure is drawn as the clinician sees the patient.
 * Face on, the patient's RIGHT is on the viewer's LEFT; from behind, the
 * patient's right is on the viewer's right. The letters R and L on the figure
 * are the PATIENT's sides, and `direction: ltr` keeps the Arabic interface
 * from mirroring them. The geometry lives in `zone-geometry.ts`.
 */
export function ZoneChart({
  patientId,
  canWrite,
  canUpload,
  specialties,
}: {
  patientId: string;
  canWrite: boolean;
  canUpload: boolean;
  specialties: string[];
}) {
  const { t, locale, formatDate } = useI18n();
  const [data, setData] = useState<ZoneChartData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [region, setRegion] = useState<'face' | 'body'>(specialties.includes('aesthetics') || !specialties.includes('dermatology') ? 'face' : 'body');
  const [view, setView] = useState<'front' | 'back'>('front');
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'finding' | 'procedure' | 'photo' | null>(null);
  const [nonce, setNonce] = useState(0);
  const [viewing, setViewing] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.zones(patientId));
    } catch (e) {
      setError(describe(e, t));
    }
  }, [patientId, t]);

  useEffect(() => {
    setData(null);
    setSelected(null);
    void load();
  }, [load]);
  useEffect(() => {
    if (nonce > 0) void load();
  }, [load, nonce]);

  const zoneNames = t.zones.zone as Record<string, string>;
  const groupNames = t.zones.group as Record<string, string>;
  const findingNames = t.zones.finding as Record<string, string>;
  const markNames = t.zones.mark as Record<string, string>;
  const unitNames = t.zones.unit as Record<string, string>;
  const severityNames = t.zones.severity as Record<string, string>;

  const byZone = useMemo(() => {
    const m = new Map<string, { finding?: ZoneChartData['zones'][number]; procedures: ZoneChartData['procedures']; images: ZoneChartData['images'] }>();
    const get = (z: string) => {
      let e = m.get(z);
      if (!e) {
        e = { procedures: [], images: [] };
        m.set(z, e);
      }
      return e;
    };
    for (const z of data?.zones ?? []) get(z.zone).finding = z;
    for (const p of data?.procedures ?? []) get(p.zone).procedures.push(p);
    for (const i of data?.images ?? []) if (i.zone) get(i.zone).images.push(i);
    return m;
  }, [data]);

  if (error && !data) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;
  if (!data) {
    return (
      <div className="card">
        <Skeleton rows={8} />
      </div>
    );
  }

  // The zones of this view, and the same zones grouped for the list.
  const zones = (region === 'face' ? FACE_ZONES : BODY_ZONES).filter((z) => region === 'face' || z.view === 'both' || z.view === view);
  const groups: { key: string; zones: Zone[] }[] = [];
  for (const z of zones) {
    const g = groups.find((x) => x.key === z.group);
    if (g) g.zones.push(z);
    else groups.push({ key: z.group, zones: [z] });
  }
  const size = region === 'face' ? FACE_SIZE : BODY_SIZE;
  const current = selected ? byZone.get(selected) : undefined;
  const selectedZone = selected ? [...FACE_ZONES, ...BODY_ZONES].find((z) => z.code === selected) ?? null : null;
  const zoneTreatments = (data.treatments ?? []).filter((x) => x.zone === selected);
  const doseText = (qty: number | null | undefined, unit: string | null | undefined) =>
    qty === null || qty === undefined ? '' : `${qty} ${unit ? (unitNames[unit] ?? unit) : ''}`.trim();
  const stateOf = (code: string) => {
    const s = byZone.get(code);
    return { procedures: s?.procedures.length ?? 0, finding: Boolean(s?.finding && s.finding.finding !== 'normal') };
  };
  const select = (code: string) => setSelected(code);
  const viewerImages: ViewerImage[] = (current?.images ?? []).map((img) => ({ id: img.attachmentId, kind: img.kind, at: String(img.at), zone: img.zone }));
  // What the caption under the figure says: the zone under the pointer, else the selected one, else how to begin.
  const captionZone = hovered ?? selected;

  return (
    <>
      <div className="chart-layout zonechart">
        <div className="card">
          <div className="card-head">
            <div>
              <h2>{t.zones.title}</h2>
              <div className="page-sub">{t.zones.subtitle}</div>
            </div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <div className="segmented" role="group" aria-label={t.zones.region}>
                {/* The face has no back: leaving the body's back view for the face would mirror it. */}
                {(['face', 'body'] as const).map((r) => (
                  <button key={r} type="button" onClick={() => { setRegion(r); if (r === 'face') setView('front'); setSelected(null); setHovered(null); }} aria-pressed={r === region}>
                    {t.zones[r]}
                  </button>
                ))}
              </div>
              {region === 'body' ? (
                <div className="segmented" role="group" aria-label={t.zones.view}>
                  {(['front', 'back'] as const).map((v) => (
                    <button key={v} type="button" onClick={() => { setView(v); setHovered(null); }} aria-pressed={v === view}>
                      {t.zones[v]}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className="card-body zonechart-body">
            {/* Never mirrored: the patient's sides are the patient's, whatever the page direction. */}
            <div className={`zonefig zonefig-${region}`} style={{ direction: 'ltr' }} onMouseLeave={() => setHovered(null)}>
              <svg viewBox={`0 0 ${size.width} ${size.height}`} className="zonefig-svg" role="group" aria-label={t.zones[region]}>
                {region === 'face' ? <FaceDrawing /> : <BodyDrawing view={view} />}
                <text x="16" y="24" className="zonefig-side">{view === 'back' ? t.zones.sideL : t.zones.sideR}</text>
                <text x={size.width - 16} y="24" className="zonefig-side" textAnchor="end">{view === 'back' ? t.zones.sideR : t.zones.sideL}</text>
                <g className="zone-layer">
                  {zones.map((z) => {
                    const d = zonePath(z.code, region, view);
                    const a = zoneAnchor(z.code, region, view);
                    if (!d || !a) return null;
                    const st = stateOf(z.code);
                    const classes = ['zone'];
                    if (st.finding) classes.push('has-finding');
                    if (st.procedures) classes.push('has-procedure');
                    if (z.code === selected) classes.push('selected');
                    if (z.code === hovered) classes.push('hover');
                    return (
                      <g
                        key={z.code}
                        className={classes.join(' ')}
                        role="button"
                        tabIndex={0}
                        aria-pressed={z.code === selected}
                        aria-label={zoneNames[z.code] ?? z.code}
                        onClick={() => select(z.code)}
                        onMouseEnter={() => setHovered(z.code)}
                        onFocus={() => setHovered(z.code)}
                        onBlur={() => setHovered(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            select(z.code);
                          }
                        }}
                      >
                        <title>{zoneNames[z.code] ?? z.code}</title>
                        <path d={d} fillRule="evenodd" />
                        {st.procedures ? (
                          <g className="zone-count" transform={`translate(${a[0]} ${a[1]})`}>
                            <circle r="8" />
                            <text y="3" textAnchor="middle">{st.procedures}</text>
                          </g>
                        ) : st.finding ? (
                          <circle className="zone-dot" cx={a[0]} cy={a[1]} r="4" />
                        ) : null}
                      </g>
                    );
                  })}
                </g>
              </svg>
              <div className="zonefig-caption" aria-live="polite">
                {captionZone ? <strong>{zoneNames[captionZone] ?? captionZone}</strong> : <span>{t.zones.hoverHint}</span>}
              </div>
              <p className="zonefig-note">{view === 'back' ? t.zones.viewNoteBack : t.zones.viewNote}</p>
            </div>

            <div className="zone-list">
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div className="section-label">{t.zones.zoneList}</div>
                <div className="odontogram-key" aria-hidden="true" style={{ margin: 0 }}>
                  <span className="odontogram-key-item"><i className="zonekey zonekey-finding" /> {t.zones.legendFinding}</span>
                  <span className="odontogram-key-item"><i className="zonekey zonekey-procedure" /> {t.zones.legendProcedure}</span>
                </div>
              </div>
              {groups.map((g) => (
                <div key={g.key} className="zone-group">
                  <div className="zone-group-name">{groupNames[g.key] ?? g.key}</div>
                  <div className="zone-chips">
                    {g.zones.map((z) => {
                      const st = stateOf(z.code);
                      const classes = ['zone-chip'];
                      if (st.finding) classes.push('has-finding');
                      if (st.procedures) classes.push('has-procedure');
                      if (z.code === selected) classes.push('selected');
                      if (z.code === hovered) classes.push('hover');
                      return (
                        <button
                          key={z.code}
                          type="button"
                          className={classes.join(' ')}
                          aria-pressed={z.code === selected}
                          onClick={() => select(z.code)}
                          onMouseEnter={() => setHovered(z.code)}
                          onMouseLeave={() => setHovered(null)}
                          onFocus={() => setHovered(z.code)}
                          onBlur={() => setHovered(null)}
                        >
                          {zoneNames[z.code] ?? z.code}
                          {st.procedures ? <span className="zone-chip-count">{st.procedures}</span> : st.finding ? <i className="zone-chip-dot" aria-hidden="true" /> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{selectedZone ? zoneNames[selectedZone.code] ?? selectedZone.code : t.zones.selectZone}</h2>
          </div>
          {!selectedZone ? (
            <div className="empty">
              <div className="empty-title">{t.zones.selectZone}</div>
              <div>{t.zones.selectZoneHint}</div>
            </div>
          ) : (
            <div className="card-body stack" style={{ gap: 16 }}>
              <div>
                <div className="section-label">{t.zones.currentFinding}</div>
                <div className="row" style={{ marginTop: 6, flexWrap: 'wrap', gap: 8 }}>
                  {current?.finding ? (
                    <>
                      <span className={`badge ${current.finding.finding === 'normal' ? 'ok' : 'warn'}`}>
                        {findingNames[current.finding.finding ?? ''] ?? current.finding.finding}
                      </span>
                      {current.finding.severity ? <span className="badge">{severityNames[current.finding.severity] ?? current.finding.severity}</span> : null}
                      <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>{formatDate(String(current.finding.effectiveAt))}</span>
                    </>
                  ) : (
                    <span style={{ color: 'var(--ink-3)' }}>{t.zones.noFinding}</span>
                  )}
                </div>
                {current?.finding?.note ? <p style={{ color: 'var(--ink-2)', marginTop: 8, marginBottom: 0 }}>{current.finding.note}</p> : null}
              </div>

              {canWrite ? (
                <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
                  <button type="button" className="btn btn-sm" onClick={() => setDialog('finding')}>{t.zones.recordFinding}</button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDialog('procedure')}>{t.zones.addProcedure}</button>
                  {canUpload ? (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDialog('photo')}>{t.zones.addPhoto}</button>
                  ) : null}
                </div>
              ) : null}

              <div>
                <div className="section-label">{t.zones.procedures}</div>
                {!current?.procedures.length ? (
                  <p style={{ color: 'var(--ink-3)', marginTop: 6 }}>{t.zones.noProcedures}</p>
                ) : (
                  <ul className="zone-procs">
                    {current.procedures.map((p) => (
                      <li key={p.id}>
                        <div className="row" style={{ justifyContent: 'space-between', gap: 10 }}>
                          <strong>{markNames[p.mark ?? ''] ?? p.mark}</strong>
                          <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>{formatDate(String(p.effectiveAt))}</span>
                        </div>
                        <div style={{ color: 'var(--ink-2)', fontSize: 13 }}>
                          {String(p.detail.procedure ?? '')}
                          {doseText(p.detail.doseQty as number | null, p.detail.doseUnit as string | null) ? ` · ${doseText(p.detail.doseQty as number | null, p.detail.doseUnit as string | null)}` : ''}
                        </div>
                        {p.detail.productLabel || p.detail.lotNumber ? (
                          <div className="ltr-value" style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                            {String(p.detail.productLabel ?? '')}
                            {p.detail.lotNumber ? ` · ${t.zones.lot} ${String(p.detail.lotNumber)}` : ''}
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {zoneTreatments.some((x) => x.status !== 'completed') ? (
                <div>
                  <div className="section-label">{t.zones.planned}</div>
                  <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
                    {zoneTreatments.filter((x) => x.status !== 'completed').map((x) => (
                      <li key={x.id}>
                        {locale === 'ar' ? x.nameAr : x.nameEn}
                        {doseText(x.doseQty, x.doseUnit) ? ` · ${doseText(x.doseQty, x.doseUnit)}` : ''} ·{' '}
                        <span className="badge">{(t.treatmentStatus as Record<string, string>)[x.status] ?? x.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div>
                <div className="section-label">{t.zones.photos}</div>
                {!current?.images.length ? (
                  <p style={{ color: 'var(--ink-3)', marginTop: 6 }}>{t.zones.noPhotos}</p>
                ) : (
                  <div className="thumb-grid">
                    {current.images.map((img, i) => (
                      <button key={img.linkId} type="button" className="thumb thumb-btn" onClick={() => setViewing(i)}>
                        <img src={`/api/imaging/file/${img.attachmentId}`} alt={`${img.kind} ${zoneNames[img.zone ?? ''] ?? ''}`} loading="lazy" />
                        <span className="thumb-meta">{formatDate(String(img.at))}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {viewing !== null && viewerImages[viewing] ? (
        <ImageViewer images={viewerImages} index={viewing} onClose={() => setViewing(null)} onIndexChange={setViewing} canAnnotate={canUpload} />
      ) : null}
      {dialog === 'photo' && selected ? (
        <ImagingUpload
          patientId={patientId}
          defaultZone={selected}
          onClose={() => setDialog(null)}
          onUploaded={() => {
            setDialog(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
      {(dialog === 'finding' || dialog === 'procedure') && selected ? (
        <ZoneDialog
          kind={dialog}
          patientId={patientId}
          zone={selected}
          zoneName={zoneNames[selected] ?? selected}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}

/** A head and neck seen face on, drawn as a light line illustration. */
function FaceDrawing() {
  const D = FACE_DRAWING;
  return (
    <g className="zonefig-art">
      <path className="zonefig-skin" d={D.head} />
      {D.ears.map((d, i) => <path key={`ear${i}`} d={d} />)}
      <path className="zonefig-soft" d={D.hairline} />
      {D.brows.map((d, i) => <path key={`brow${i}`} className="zonefig-brow" d={d} />)}
      {D.eyes.map((d, i) => <path key={`eye${i}`} d={d} />)}
      {D.irises.map((c, i) => <circle key={`iris${i}`} cx={c.cx} cy={c.cy} r={c.r} />)}
      {D.nose.map((d, i) => <path key={`nose${i}`} d={d} />)}
      {D.mouth.map((d, i) => <path key={`mouth${i}`} d={d} />)}
      {D.neck.map((d, i) => <path key={`neck${i}`} className="zonefig-soft" d={d} />)}
      {D.shoulders.map((d, i) => <path key={`sh${i}`} className="zonefig-soft" d={d} />)}
    </g>
  );
}

/** A standing figure, front or back; the same silhouette, different landmarks. */
function BodyDrawing({ view }: { view: 'front' | 'back' }) {
  const D = BODY_DRAWING;
  return (
    <g className="zonefig-art">
      <path className="zonefig-skin" d={D.body} />
      <ellipse className="zonefig-skin" cx={D.head.cx} cy={D.head.cy} rx={D.head.rx} ry={D.head.ry} />
      {D[view].map((d, i) => <path key={`${view}${i}`} className="zonefig-soft" d={d} />)}
    </g>
  );
}

function ZoneDialog({
  kind,
  patientId,
  zone,
  zoneName,
  onClose,
  onDone,
}: {
  kind: 'finding' | 'procedure';
  patientId: string;
  zone: string;
  zoneName: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t, tx, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finding, setFinding] = useState('wrinkles_dynamic');
  const [severity, setSeverity] = useState('');
  const [note, setNote] = useState('');
  const [catalogue, setCatalogue] = useState<CatalogueItem[]>([]);
  const [catalogueId, setCatalogueId] = useState('');
  const [price, setPrice] = useState('');
  const [status, setStatus] = useState<'planned' | 'completed'>('completed');
  const [doseQty, setDoseQty] = useState('');
  const [doseUnit, setDoseUnit] = useState('');
  const [productLabel, setProductLabel] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [billable, setBillable] = useState(true);
  const [performedOn, setPerformedOn] = useState(() => new Date().toISOString().slice(0, 10));

  const chosen = catalogue.find((c) => c.id === catalogueId) ?? null;
  const findingNames = t.zones.finding as Record<string, string>;
  const markNames = t.zones.mark as Record<string, string>;
  const unitNames = t.zones.unit as Record<string, string>;
  const severityNames = t.zones.severity as Record<string, string>;

  useEffect(() => {
    if (kind !== 'procedure') return;
    void api
      .catalogue()
      .then((r) => {
        // Zone procedures first; a general item (a consultation) is allowed too.
        const items = r.items.filter((c) => c.requiresZone || (c.specialty !== 'dental' && !c.requiresTooth));
        setCatalogue(items);
        const first = items.find((c) => c.requiresZone) ?? items[0];
        if (first) {
          setCatalogueId(first.id);
          setPrice(minorToInput(first.priceMinor, first.currency));
          setDoseUnit(first.doseUnit ?? '');
        }
      })
      .catch(() => setError(t.common.error));
  }, [kind, t]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      if (kind === 'finding') {
        await api.recordZoneObservation(patientId, { zone, finding, severity: severity || null, note: note || undefined });
      } else {
        if (!chosen) throw new Error('no item');
        const priceMinor = parseToMinor(price, chosen.currency);
        if (priceMinor === null) {
          setError(t.chart.priceInvalid);
          return;
        }
        const qty = doseQty.trim() === '' ? null : Number(doseQty.replace(',', '.'));
        if (qty !== null && (!Number.isFinite(qty) || qty < 0)) {
          setError(t.zones.doseInvalid);
          return;
        }
        await api.createTreatment({
          patientId,
          catalogueId: chosen.id,
          nameEn: chosen.nameEn,
          nameAr: chosen.nameAr,
          zone,
          doseQty: qty,
          doseUnit: doseUnit || null,
          productLabel: productLabel.trim() || null,
          lotNumber: lotNumber.trim() || null,
          priceMinor,
          currency: chosen.currency,
          status,
          billable,
          performedAt: status === 'completed' ? `${performedOn}T12:00:00.000Z` : undefined,
          notes: note || undefined,
        });
      }
      onDone();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`${kind === 'finding' ? t.zones.recordFinding : t.zones.addProcedure} — ${zoneName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{t.patients.cancel}</button>
          <button type="button" className="btn" onClick={() => void submit()} disabled={busy || (kind === 'procedure' && !chosen)}>
            {busy ? (<><Spinner /> {t.patients.saving}</>) : t.chart.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}

        {kind === 'finding' ? (
          <div className="grid-2">
            <Field label={t.zones.findingLabel} required>
              <select value={finding} onChange={(e) => setFinding(e.target.value)}>
                {Object.keys(findingNames).map((k) => (
                  <option key={k} value={k}>{findingNames[k]}</option>
                ))}
              </select>
            </Field>
            <Field label={t.zones.severityLabel}>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="">—</option>
                {Object.keys(severityNames).map((k) => (
                  <option key={k} value={k}>{severityNames[k]}</option>
                ))}
              </select>
            </Field>
          </div>
        ) : (
          <>
            {catalogue.length === 0 ? <div className="notice warn"><span>{t.zones.noProcedureItems}</span></div> : null}
            <Field label={t.chart.procedure} required>
              <select
                value={catalogueId}
                onChange={(e) => {
                  setCatalogueId(e.target.value);
                  const next = catalogue.find((c) => c.id === e.target.value);
                  if (next) {
                    setPrice(minorToInput(next.priceMinor, next.currency));
                    setDoseUnit(next.doseUnit ?? '');
                  }
                }}
              >
                {catalogue.map((c) => (
                  <option key={c.id} value={c.id}>{c.code} · {locale === 'ar' ? c.nameAr : c.nameEn}</option>
                ))}
              </select>
            </Field>
            <div className="grid-2">
              <Field label={t.zones.dose} hint={t.zones.doseHint}>
                <input value={doseQty} onChange={(e) => setDoseQty(e.target.value)} inputMode="decimal" className="ltr-value" placeholder="20" />
              </Field>
              <Field label={t.zones.doseUnit}>
                <select value={doseUnit} onChange={(e) => setDoseUnit(e.target.value)}>
                  <option value="">—</option>
                  {Object.keys(unitNames).map((k) => (
                    <option key={k} value={k}>{unitNames[k]}</option>
                  ))}
                </select>
              </Field>
              <Field label={t.zones.product} hint={t.zones.productHint}>
                <input value={productLabel} onChange={(e) => setProductLabel(e.target.value)} className="ltr-value" />
              </Field>
              <Field label={t.zones.lot} hint={t.zones.lotHint}>
                <input value={lotNumber} onChange={(e) => setLotNumber(e.target.value)} className="ltr-value" />
              </Field>
              <Field
                label={`${t.chart.price}${chosen ? ` (${chosen.currency})` : ''}`}
                hint={chosen && parseToMinor(price, chosen.currency) !== chosen.priceMinor ? `${t.chart.cataloguePrice}: ${formatMinor(chosen.priceMinor, chosen.currency, locale)}` : t.chart.priceHint}
                required
              >
                <input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" className="ltr-value" />
              </Field>
              <Field label={t.common.status} required>
                <select value={status} onChange={(e) => setStatus(e.target.value as 'planned' | 'completed')}>
                  <option value="completed">{t.treatmentStatus.completed}</option>
                  <option value="planned">{t.treatmentStatus.planned}</option>
                </select>
              </Field>
            </div>
            <label className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                <strong>{t.chart.chargeable}</strong>
                <span className="sub" style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>{t.chart.chargeableHint}</span>
              </span>
            </label>
            {status === 'completed' ? (
              <Field label={t.chart.performedOn} hint={t.chart.performedOnHint}>
                <input type="date" value={performedOn} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setPerformedOn(e.target.value)} className="ltr-value" />
              </Field>
            ) : null}
            {status === 'completed' && chosen?.zoneMark ? (
              <div className="notice"><span>{tx(t.zones.willMark, { zone: zoneName, mark: markNames[chosen.zoneMark] ?? chosen.zoneMark })}</span></div>
            ) : null}
          </>
        )}

        <Field label={t.patients.notes}>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
        </Field>
      </div>
    </Modal>
  );
}
